import type { AxiosError } from 'axios';
import { Button, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { Space } from '@/api/generated/models';
import {
  getListSpacesQueryKey,
  useCreateSpace,
  useUpdateSpace
} from '@/api/generated/work-structure/work-structure';
import { FormModal, ModalFooter, MutationButton, TextField, Toast } from '@/components/common';

type FormValues = { name: string; code: string };

const emptyValues: FormValues = { name: '', code: '' };

const spaceErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface SpaceFormModalProps {
  opened: boolean;
  onClose: () => void;
  objectId: string;
  parentSpaceId: string | null;
  parentLabel: string;
  space?: Space;
  existingSpaces?: Space[];
  onSaved?: (message: string) => void;
}

export const SpaceFormModal = ({
  opened,
  onClose,
  objectId,
  parentSpaceId,
  parentLabel,
  space,
  existingSpaces = [],
  onSaved
}: SpaceFormModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!space;

  // Naziv/šifra moraju biti jedinstveni samo među BRAĆOM (isti parentSpaceId) — isti naziv
  // podsegmenta smije postojati pod različitim nadređenim segmentima. Provjera je dio SHEME
  // (superRefine), isti obrazac kao ObjectFormModal. Backend je i dalje konačan izvor istine
  // (space.duplicate, vidi WorkStructureService.assertUniqueNameAndCode).
  const schema = z
    .object({
      name: z.string().min(1),
      code: isEdit ? z.string().min(1) : z.string().optional()
    })
    .superRefine((data, ctx) => {
      const trimmedName = data.name.trim().toLowerCase();
      const trimmedCode = data.code?.trim().toLowerCase();
      const others = existingSpaces.filter(
        (s) => s.id !== space?.id && (s.parentSpaceId ?? null) === (parentSpaceId ?? null)
      );

      if (others.some((s) => (s.name ?? '').trim().toLowerCase() === trimmedName)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['name'],
          message: t('spaceTree.nameDuplicate')
        });
      }
      if (trimmedCode && others.some((s) => (s.code ?? '').trim().toLowerCase() === trimmedCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['code'],
          message: t('spaceTree.codeDuplicate')
        });
      }
    });

  const form = useForm<FormValues>({ validate: zodResolver(schema), initialValues: emptyValues });

  useEffect(() => {
    if (opened) {
      form.setValues(space ? { name: space.name ?? '', code: space.code ?? '' } : emptyValues);
    }
  }, [opened, space]);

  const createMutation = useCreateSpace({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListSpacesQueryKey(objectId) });
        onSaved?.(t('spaceTree.createSuccess'));
        onClose();
      }
    }
  });

  const updateMutation = useUpdateSpace({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListSpacesQueryKey(objectId) });
        onSaved?.(t('spaceTree.updateSuccess'));
        onClose();
      }
    }
  });

  const mutation = isEdit ? updateMutation : createMutation;

  const handleSubmit = (values: FormValues) => {
    if (isEdit && space?.id) {
      updateMutation.mutate({
        id: space.id,
        data: {
          code: values.code,
          name: values.name,
          description: space.description,
          sortOrder: space.sortOrder
        }
      });
    } else {
      createMutation.mutate({
        objectId,
        data: {
          parentSpaceId: parentSpaceId ?? undefined,
          name: values.name,
          code: values.code || undefined
        }
      });
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {isEdit ? t('spaceTree.editModalTitle') : t('spaceTree.addModalTitle')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={16}>
          <Stack gap={4}>
            <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}>
              {t('spaceTree.parentLabel')}
            </Text>
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 6,
                backgroundColor: 'var(--color-bg-canvas)',
                border: '1px solid var(--color-border-soft)'
              }}
            >
              <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {parentLabel}
              </Text>
            </div>
          </Stack>

          {mutation.isError && (
            <Toast tone="danger">{spaceErrorMessage(mutation.error, t('common.error'))}</Toast>
          )}

          <TextField label={t('spaceTree.nameLabel')} required {...form.getInputProps('name')} />

          <TextField
            label={t('spaceTree.codeLabel')}
            required={isEdit}
            {...form.getInputProps('code')}
          />

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              {t('spaceTree.cancel')}
            </Button>
            <MutationButton
              type="submit"
              isPending={mutation.isPending}
              isPaused={mutation.isPaused}
            >
              {isEdit ? t('spaceTree.editSubmit') : t('spaceTree.submit')}
            </MutationButton>
          </ModalFooter>
        </Stack>
      </form>
    </FormModal>
  );
};
